"use client";

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { GlassPanel } from "@/components/ui/GlassPanel";
import { SectionHeading } from "@/components/ui/SectionHeading";
import { Button } from "@/components/ui/button";
import type { UsageSummary } from "../types";
import { TIER_CREDIT_LIMITS, type SubscriptionTier } from "@/lib/stripe/config";

interface UsageSummaryCardProps {
  summary: UsageSummary | null;
  isLoading: boolean;
  error?: string | null;
  onRetry?: () => void;
}

// Tier pricing (monthly USD)
const TIER_PRICING: Record<SubscriptionTier, number> = {
  free: 0,
  pro: 20,
  pro_plus: 60,
  dev: 0,
};

function formatDate(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

export function UsageSummaryCard({ summary, isLoading, error, onRetry }: UsageSummaryCardProps) {
  const { t } = useTranslation();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [targetUpgradeTier, setTargetUpgradeTier] = useState<'pro' | 'pro_plus' | null>(null);
  const [isUpgrading, setIsUpgrading] = useState(false);

  const usageSinceLabel = useMemo(() => formatDate(summary?.usageSince ?? null), [summary?.usageSince]);
  const localizedTierLabel = useMemo(() => {
    if (!summary) return null;
    const tierKey = summary.tier ?? "";
    if (!tierKey) return summary.tierLabel;
    return t(`account.usage.plan_names.${tierKey}`, summary.tierLabel);
  }, [summary, t]);

  const currentTier = summary?.tier ?? "free";

  const handleCreateCheckout = (plan: 'pro' | 'pro_plus') => {
    if (typeof window === "undefined") return;

    fetch('/api/stripe/create-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan }),
    })
      .then(async res => {
        if (!res.ok) {
          toast.error('Failed to create checkout session');
          return null;
        }
        return res.json();
      })
      .then(data => {
        if (data?.url) {
          window.location.href = data.url;
        }
      })
      .catch(err => {
        console.error('Error:', err);
        toast.error('Failed to create checkout session');
      });
  };

  const handleUpgradeClick = (targetTier: 'pro' | 'pro_plus') => {
    setTargetUpgradeTier(targetTier);
    setShowUpgradeDialog(true);
  };

  const handleConfirmUpgrade = async () => {
    if (!targetUpgradeTier || typeof window === "undefined") return;

    setIsUpgrading(true);
    try {
      const res = await fetch('/api/stripe/upgrade-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetTier: targetUpgradeTier }),
      });

      if (!res.ok) {
        const data = await res.json();
        toast.error(data.error || 'Failed to upgrade subscription');
        setShowUpgradeDialog(false);
        setIsUpgrading(false);
        return;
      }

      toast.success(t("account.usage.upgrade_success", "Subscription upgraded successfully!"));
      setShowUpgradeDialog(false);
      // Reload to refresh tier display
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.error('Error upgrading subscription:', err);
      toast.error('Failed to upgrade subscription');
      setIsUpgrading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setIsCanceling(true);
    try {
      const res = await fetch('/api/stripe/cancel-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) {
        throw new Error('Failed to cancel subscription');
      }

      toast.success(t("account.usage.cancel_dialog.success", "Subscription canceled successfully"));
      setShowCancelDialog(false);

      // Reload page to refresh subscription status
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.error('Error canceling subscription:', err);
      toast.error(t("account.usage.cancel_dialog.error"));
    } finally {
      setIsCanceling(false);
    }
  };

  if (!summary && !isLoading && !error) {
    return null;
  }

  return (
    <GlassPanel variant="solid" className="p-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <SectionHeading>{t("account.usage.title", "Usage Summary")}</SectionHeading>
        {usageSinceLabel && (
          <span className="text-sm text-neutral-300">
            {t("account.usage.usage_since", { date: usageSinceLabel })}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="mt-6 flex items-center gap-2 text-neutral-300">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>{t("account.usage.loading", "Loading usage data…")}</span>
        </div>
      ) : summary ? (
        <div className="mt-5 space-y-6">
          <div className="text-sm text-neutral-300">
            {t("account.usage.plan_label", "Plan")}: {" "}
            <span className="font-semibold text-neutral-100">{localizedTierLabel ?? summary.tierLabel}</span>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:items-stretch">
            <div className="space-y-4 h-full">
              {summary.entries.map((entry) => {
                const isUnused = (entry.used ?? 0) <= 0;
                const formattedLimit = entry.limit != null ? entry.limit.toLocaleString() : null;
                const remaining = Math.max(entry.remaining ?? 0, 0);

                return (
                  <div key={entry.key} className="rounded-lg border border-white/10 bg-white/5 p-4 h-full flex flex-col">
                    <div className="text-sm font-medium uppercase tracking-wide text-neutral-300">
                      {entry.key === "usage-credits"
                        ? t("account.usage.credits_title", "Usage Credits")
                        : entry.title}
                    </div>
                    <div className="mt-2 text-3xl font-semibold text-white">
                      {isUnused ? t("account.usage.unused", "Unused") : entry.used.toLocaleString()}
                      {!isUnused && (
                        <span className="ml-2 text-sm font-normal text-neutral-300">
                          {t("account.usage.of_limit_used", {
                            limit: formattedLimit != null ? formattedLimit : "—",
                          })}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-sm text-neutral-300">
                      {entry.limit != null
                        ? isUnused
                          ? t("account.usage.available", { limit: formattedLimit ?? "0" })
                          : t("account.usage.left", "{{count}} left", { count: remaining })
                        : t("account.usage.unlimited", "Unlimited")}
                    </div>
                    {entry.description && (
                      <p className="mt-3 text-xs leading-relaxed text-neutral-400">
                        {entry.key === "usage-credits"
                          ? t("account.usage.credits_description", {
                              tier: localizedTierLabel ?? summary.tierLabel,
                            })
                          : entry.description}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="rounded-lg border border-white/10 bg-white/5 p-4 flex flex-col justify-between h-full">
              <div>
                <div className="text-sm font-medium uppercase tracking-wide text-neutral-300">
                  {t("account.usage.need_more.title", "Need more?")}
                </div>
                <p className="mt-3 text-sm leading-relaxed text-neutral-300">
                  {t("account.usage.need_more.description")}
                </p>
              </div>
              <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-stretch">
                {/* Free tier: Show both upgrade buttons */}
                {currentTier === "free" && (
                  <>
                    <Button
                      variant="highlight"
                      className="flex-1 whitespace-normal h-auto py-3 min-h-[44px]"
                      onClick={() => handleCreateCheckout('pro')}
                    >
                      {t("account.usage.need_more.upgrade_pro", "Upgrade to Pro")}
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1 whitespace-normal h-auto py-3 min-h-[44px]"
                      onClick={() => handleCreateCheckout('pro_plus')}
                    >
                      {t("account.usage.need_more.upgrade_pro_plus", "Upgrade to Pro+")}
                    </Button>
                  </>
                )}

                {/* Pro tier: Show Upgrade to Pro+ and Cancel */}
                {currentTier === "pro" && (
                  <>
                    <Button
                      variant="highlight"
                      className="flex-1 whitespace-normal h-auto py-3 min-h-[44px]"
                      onClick={() => handleUpgradeClick('pro_plus')}
                    >
                      {t("account.usage.need_more.upgrade_pro_plus", "Upgrade to Pro+")}
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1 whitespace-normal h-auto py-3 min-h-[44px]"
                      onClick={() => setShowCancelDialog(true)}
                    >
                      {t("account.usage.need_more.cancel_subscription", "Cancel Subscription")}
                    </Button>
                  </>
                )}

                {/* Pro+ tier: Show Contact Us and Cancel */}
                {currentTier === "pro_plus" && (
                  <>
                    <Button
                      variant="highlight"
                      className="flex-1 whitespace-normal h-auto py-3 min-h-[44px]"
                      onClick={() => {
                        window.location.href = "mailto:support@lema-app.com?subject=Request for More Credits";
                      }}
                    >
                      {t("account.usage.need_more.contact_for_credits", "Contact Us for More Credits")}
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1 whitespace-normal h-auto py-3 min-h-[44px]"
                      onClick={() => setShowCancelDialog(true)}
                    >
                      {t("account.usage.need_more.cancel_subscription", "Cancel Subscription")}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-6 rounded-lg border border-white/10 bg-white/5 p-4 text-neutral-300">
          <p className="text-sm">
            {t("account.usage.error_unable_to_load", "Unable to load usage data right now.")}
          </p>
          {error && <p className="mt-1 text-xs text-red-300">{error}</p>}
          {onRetry && (
            <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
              {t("account.usage.retry", "Retry")}
            </Button>
          )}
        </div>
      )}

      {/* Cancellation Confirmation Dialog */}
      {showCancelDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg border border-white/10 bg-neutral-900 p-6 shadow-xl">
            <h3 className="text-xl font-semibold text-white">
              {t("account.usage.cancel_dialog.title")}
            </h3>
            <p className="mt-3 text-sm text-neutral-300">
              {t("account.usage.cancel_dialog.message")}
            </p>
            <div className="mt-6 flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowCancelDialog(false)}
                disabled={isCanceling}
              >
                {t("account.usage.cancel_dialog.cancel")}
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={handleCancelSubscription}
                disabled={isCanceling}
              >
                {isCanceling ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("account.usage.cancel_dialog.canceling")}
                  </>
                ) : (
                  t("account.usage.cancel_dialog.confirm")
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Confirmation Dialog */}
      {showUpgradeDialog && targetUpgradeTier && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg border border-white/10 bg-neutral-900 p-6 shadow-xl">
            <h3 className="text-xl font-semibold text-white">
              {t("account.usage.upgrade_dialog.title")}
            </h3>
            <p className="mt-4 text-sm text-neutral-300">
              {t("account.usage.upgrade_dialog.message", {
                currentPlan: currentTier === 'pro' ? 'Pro' : currentTier === 'pro_plus' ? 'Pro+' : 'Free',
                currentPrice: TIER_PRICING[currentTier],
                newPlan: targetUpgradeTier === 'pro' ? 'Pro' : 'Pro+',
                newPrice: TIER_PRICING[targetUpgradeTier],
                newCredits: TIER_CREDIT_LIMITS[targetUpgradeTier],
              })}
            </p>
            <div className="mt-6 flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowUpgradeDialog(false);
                  setTargetUpgradeTier(null);
                }}
                disabled={isUpgrading}
              >
                {t("account.usage.upgrade_dialog.cancel")}
              </Button>
              <Button
                variant="highlight"
                className="flex-1"
                onClick={handleConfirmUpgrade}
                disabled={isUpgrading}
              >
                {isUpgrading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("account.usage.upgrade_dialog.upgrading")}
                  </>
                ) : (
                  t("account.usage.upgrade_dialog.confirm")
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </GlassPanel>
  );
}
